import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { MailCheck } from 'lucide-react'
import { useAuth } from '@/context/auth'
import { useSeo } from '@/lib/useSeo'
import { AuthLayout, inputBase, labelBase, primaryButton } from '@/components/public/AuthLayout'

export function ForgotPassword() {
  useSeo({ title: 'Forgot password', description: 'Request a password reset link.' })
  const { resetPasswordForEmail } = useAuth()
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    const { error: err } = await resetPasswordForEmail(email)
    setSubmitting(false)
    if (err) {
      setError(err.message)
      return
    }
    setSent(true)
  }

  if (sent) {
    return (
      <AuthLayout title="Check your email">
        <div className="text-center">
          <div className="mx-auto inline-flex p-3 rounded-full bg-accent-sunset/10 text-accent-sunset">
            <MailCheck size={28} aria-hidden="true" />
          </div>
          <p className="mt-4 text-neutral-700">
            If <strong className="text-primary">{email}</strong> is associated with a VICE account,
            we&apos;ve sent a password reset link. The link expires in one hour.
          </p>
          <Link to="/signin" className="mt-6 inline-block text-secondary font-semibold hover:text-primary">
            Back to sign in
          </Link>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Forgot your password?" subtitle="We'll send a reset link to your email.">
      <form onSubmit={onSubmit} className="space-y-5">
        <div>
          <label htmlFor="email" className={labelBase}>
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputBase}
          />
        </div>
        {error && <p role="alert" className="text-sm text-error">{error}</p>}
        <button type="submit" disabled={submitting} className={primaryButton}>
          {submitting ? 'Sending…' : 'Send reset link'}
        </button>
        <p className="text-center text-sm text-neutral-600">
          Remembered it?{' '}
          <Link to="/signin" className="text-secondary font-semibold hover:text-primary">
            Sign in
          </Link>
        </p>
      </form>
    </AuthLayout>
  )
}
